import _ from 'lodash';
import * as DT from './model';
import { IDatatableState } from './model';

export const isInVersion = <T extends {}>(data: DT.IData<T>, version: number) =>
  data.version === version;

export const getDataPage = <T extends {}>(
  pages: DT.IDataPage<T>[],
  pageNumber: number
) => pages.filter((p) => p.pageNumber === pageNumber)[0];

export const replacePage = <T extends {}>(
  pages: DT.IDataPage<T>[],
  newPage: DT.IDataPage<any>
) => {
  const oldPage = pages.find((p) => p.pageNumber === newPage.pageNumber);
  const idx = oldPage ? pages.indexOf(oldPage) : -1;
  return idx > -1
    ? [...pages.slice(0, idx), newPage, ...pages.slice(idx + 1)]
    : [...pages, newPage];
};

export const getDataPages = <T extends {}>(
  pages: DT.IDataPage<T>[],
  firstPageNumber: number,
  lastPageNumber: number
) => {
  let dataPages: DT.IDataPage<T>[] = [];
  if (firstPageNumber < lastPageNumber)
    dataPages = getDataPages(pages, firstPageNumber + 1, lastPageNumber);
  dataPages.push(
    getDataPage(pages, firstPageNumber) || {
      ...DT.DataPageDefault<T>(),
      pageNumber: firstPageNumber,
    }
  );
  return dataPages;
};

export const addPagestoLoad = <T extends {}>(
  pagesToLoad: number[],
  pages: DT.IDataPage<T>[]
) => {
  const pageNumbersNotLoaded = pages
    .filter((dp) => !dp.isLoaded && !dp.isFetching)
    .map((dp) => dp.pageNumber);
  return [...pagesToLoad, ...pageNumbersNotLoaded];
};

export const removePageToLoad = (pagesToLoad: number[], pageNumber: number) =>
  pagesToLoad.indexOf(pageNumber) === -1
    ? pagesToLoad
    : pagesToLoad.filter((p) => p !== pageNumber);

export const recalculatePagination = (
  pagination: DT.TablePagination,
  totalRecords: number
) => {
  const numPages = _.ceil(totalRecords / pagination.pageSize);
  const currentPage = _.max([_.min([pagination.currentPage, numPages]), 1]);
  return { ...pagination, numPages, currentPage } as DT.TablePagination;
};

export const getTableContent = <T extends {}>(
  pages: DT.IDataPage<T>[],
  startRecord: number,
  endRecord: number,
  dataPageSize: number,
  tablePageSize: number
) => {
  const firstRecordDataPageNumber = _.ceil(startRecord / dataPageSize);
  const firstDataPageSkip = (startRecord - 1) % dataPageSize;
  const firstDataPageTake =
    _.min([dataPageSize - firstDataPageSkip, tablePageSize]) || 0;
  const firstDataPageEnd = firstDataPageSkip + firstDataPageTake;
  const lastRecordDataPageNumber = _.ceil(endRecord / dataPageSize);
  const lastDataPageTake =
    endRecord % dataPageSize > 0 ? endRecord % dataPageSize : dataPageSize;

  const content = pages.reduce((prev: T[], dp) => {
    if (!dp.isLoaded || dp.items.length === 0) return prev;
    if (dp.pageNumber === firstRecordDataPageNumber)
      return [...dp.items.slice(firstDataPageSkip, firstDataPageEnd), ...prev];
    if (dp.pageNumber === lastRecordDataPageNumber)
      return [...prev, ...dp.items.slice(0, lastDataPageTake)];
    return [...dp.items, ...prev];
  }, []);

  return content;
};

export const isTableItemSelected = <T extends {}>(
  o: T,
  selection: DT.ItemSelection<T>
) => selection.selectedKeys.includes(selection.getPK(o));

export const isAllTableSelected = <T extends {}>(
  table: DT.ITable<T>,
  selection: DT.ItemSelection<T>
): boolean =>
  table.content.length > 0 &&
  table.content.filter((r) => !isTableItemSelected(r, selection)).length === 0;

export const deriveState = <T extends {}>(
  state: DT.IDatatableState<T, any>
) => {
  const pagination = recalculatePagination(
    state.table.pagination,
    state.data.total
  );

  const firstRecord = (pagination.currentPage - 1) * pagination.pageSize + 1;
  const lastRecord = _.min([
    pagination.currentPage * pagination.pageSize,
    state.data.total,
  ]) as number;
  const firstRecordDataPageNumber = _.ceil(firstRecord / state.data.pageSize);
  const lastRecordDataPageNumber = _.ceil(lastRecord / state.data.pageSize);

  const dataPagesInvolved = getDataPages<T>(
    state.data.pages,
    firstRecordDataPageNumber,
    lastRecordDataPageNumber
  );

  const content = getTableContent<T>(
    dataPagesInvolved,
    firstRecord,
    lastRecord,
    state.data.pageSize,
    pagination.pageSize
  );

  const isLoading = dataPagesInvolved.filter((dp) => dp.isFetching).length > 0;
  const table = { ...state.table, pagination, content, isLoading };

  const pagesToLoad = addPagestoLoad<T>(state.pagesToLoad, dataPagesInvolved);

  const isAllSelected = isAllTableSelected(table, state.selection);
  const selection = { ...state.selection, isAllSelected };

  return { ...state, table, pagesToLoad, selection };
};

export const concatPageItems = <T extends {}>(pages: DT.IDataPage<T>[]) =>
  pages.reduce((prev, p) => {
    return [...prev, ...p.items];
  }, [] as T[]);

export const clearData = <T extends {}>(data: DT.IData<T>): DT.IData<T> => {
  return { ...data, pages: [], total: 0, version: data.version + 1 };
};

export const resetPagination = (
  pagination: DT.TablePagination
): DT.TablePagination => {
  return { ...pagination, currentPage: 1, numPages: 1 };
};

export const resetTable = <T extends {}>(table: DT.ITable<T>): DT.ITable<T> => {
  const pagination = resetPagination(table.pagination);
  return { ...table, content: [], isLoading: false, pagination };
};

export const resetSelection = <T extends {}>(
  selection: DT.ItemSelection<T>
): DT.ItemSelection<T> => ({
  ...selection,
  selectedKeys: [],
  isAllSelected: false,
});

export const resetState = <T extends {}, FC extends {}>(
  state: IDatatableState<T, FC>
): IDatatableState<T, FC> => {
  const data = clearData(state.data);
  const selection = resetSelection(state.selection);
  const table = resetTable(state.table);
  const pagesToLoad = [1];
  return { ...state, data, table, selection, pagesToLoad };
};

export const toggleColumnVisibility = (column: DT.TableColumn) => {
  column.show = !column.show;
};

export const updateColumnVisibility = (
  columns: DT.TableColumn[],
  column: DT.TableColumn
): DT.TableColumn[] => {
  const updatedColumn = { ...column };
  toggleColumnVisibility(updatedColumn);
  const index = columns.indexOf(column);
  return [
    ...columns.slice(0, index),
    updatedColumn,
    ...columns.slice(index + 1),
  ];
};

const initColumnVisibility = (column: DT.TableColumn) => {
  column.show =
    typeof column.show === 'boolean' ? column.show : column.defaultShow;
};
export const initColumnsVisibility = (columns: DT.TableColumn[]) =>
  columns.map((o) => {
    initColumnVisibility(o);
    return o;
  });

export const resetScroll = () => {
  document.getElementsByClassName('rt-tbody')[0].scrollTop = 0;
};
